import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { MultiSelect } from './multi-select';
import { useAllDiagnosticRooms } from '../../services/httpServices/queries';
import {
  X,
  Building2,
  DoorOpen,
  BedDouble,
  Activity,
  Stethoscope,
  Baby,
  HeartPulse,
  ShieldAlert,
  Hotel,
  Layers,
  AlertCircle,
} from 'lucide-react';

export type WardType = 'GENERAL' | 'SEMI_PRIVATE' | 'PRIVATE' | 'ICU' | 'NICU' | 'CCU' | 'HDU';

export interface WardFormData {
  wardCode: string;
  wardName: string;
  wardType: WardType;
  roomIds: string[];
  totalBeds: number;
  isActive: boolean;
}

interface WardFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WardFormData) => void;
  initialData?: Partial<WardFormData> & { id?: string };
  isLoading?: boolean;
}

const wardTypeConfig: Record<
  WardType,
  { label: string; icon: React.ElementType; color: string; bg: string; border: string; description: string }
> = {
  GENERAL: {
    label: 'General',
    icon: Hotel,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    border: 'border-blue-200 dark:border-blue-800',
    description: 'Standard shared ward',
  },
  SEMI_PRIVATE: {
    label: 'Semi-Private',
    icon: Layers,
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-950/40',
    border: 'border-indigo-200 dark:border-indigo-800',
    description: '2-4 beds per room',
  },
  PRIVATE: {
    label: 'Private',
    icon: Building2,
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    border: 'border-emerald-200 dark:border-emerald-800',
    description: 'Single occupancy room',
  },
  ICU: {
    label: 'ICU',
    icon: Stethoscope,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-800',
    description: 'Intensive Care Unit',
  },
  NICU: {
    label: 'NICU',
    icon: Baby,
    color: 'text-pink-600 dark:text-pink-400',
    bg: 'bg-pink-50 dark:bg-pink-950/40',
    border: 'border-pink-200 dark:border-pink-800',
    description: 'Neonatal Intensive Care',
  },
  CCU: {
    label: 'CCU',
    icon: HeartPulse,
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-950/40',
    border: 'border-rose-200 dark:border-rose-800',
    description: 'Critical Care Unit',
  },
  HDU: {
    label: 'HDU',
    icon: ShieldAlert,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800',
    description: 'High Dependency Unit',
  },
};

function generateWardCode(name: string): string {
  if (!name.trim()) return '';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase();
  }
  return words.map((w) => w[0]).join('').toUpperCase();
}

function validateForm(data: WardFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.wardCode.trim()) {
    errors.wardCode = 'Ward code is required';
  } else if (data.wardCode.length < 2) {
    errors.wardCode = 'Ward code must be at least 2 characters';
  } else if (data.wardCode.length > 20) {
    errors.wardCode = 'Ward code must be at most 20 characters';
  }
  if (!data.wardName.trim()) {
    errors.wardName = 'Ward name is required';
  } else if (data.wardName.length > 100) {
    errors.wardName = 'Ward name must be at most 100 characters';
  }
  if (!data.roomIds || data.roomIds.length === 0) {
    errors.roomIds = 'Select at least one room';
  }
  if (data.totalBeds < 1) {
    errors.totalBeds = 'Must have at least 1 bed';
  }
  if (data.totalBeds > 500) {
    errors.totalBeds = 'Cannot exceed 500 beds';
  }
  return errors;
}

export function WardFormModal({ isOpen, onClose, onSubmit, initialData, isLoading }: WardFormModalProps) {
  const isEditing = !!initialData?.id;
  const [form, setForm] = useState<WardFormData>({
    wardCode: '',
    wardName: '',
    wardType: 'GENERAL',
    roomIds: [],
    totalBeds: 1,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [autoCode, setAutoCode] = useState(!isEditing);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const { data: roomsData, isLoading: roomsLoading } = useAllDiagnosticRooms();
  const rooms = roomsData?.data || [];
  const selectedFloors = Array.from(
    new Set(
      form.roomIds
        .map((id: string) => rooms.find((room: any) => room.id === id)?.floor?.name)
        .filter(Boolean),
    ),
  ) as string[];

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setForm({
          wardCode: initialData.wardCode ?? '',
          wardName: initialData.wardName ?? '',
          wardType: initialData.wardType ?? 'GENERAL',
          roomIds: initialData.roomIds ?? [],
          totalBeds: initialData.totalBeds ?? 1,
          isActive: initialData.isActive ?? true,
        });
        setAutoCode(false);
      } else {
        setForm({
          wardCode: '',
          wardName: '',
          wardType: 'GENERAL',
          roomIds: [],
          totalBeds: 1,
          isActive: true,
        });
        setAutoCode(true);
      }
      setErrors({});
      setTouched({});
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [isOpen, initialData]);

  const updateField = useCallback(<K extends keyof WardFormData>(field: K, value: WardFormData[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'wardName' && autoCode && !isEditing) {
        next.wardCode = generateWardCode(value as string);
      }
      return next;
    });
    if (touched[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [autoCode, isEditing, touched]);

  const touchField = useCallback((field: keyof WardFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleBlur = useCallback((field: keyof WardFormData) => {
    touchField(field);
    const fieldErrors = validateForm(form);
    if (fieldErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [form, touchField]);

  const handleRoomsChange = useCallback((ids: string[]) => {
    setForm((prev) => ({ ...prev, roomIds: ids }));
    setTouched((prev) => ({ ...prev, roomIds: true }));
    setErrors((prev) => {
      if (!prev.roomIds) return prev;
      const next = { ...prev };
      delete next.roomIds;
      return next;
    });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched: Record<string, boolean> = {
      wardCode: true,
      wardName: true,
      roomIds: true,
      totalBeds: true,
    };
    setTouched(allTouched);
    const fieldErrors = validateForm(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    onSubmit(form);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">
                {isEditing ? 'Edit Ward' : 'New Ward'}
              </h2>
              <p className="text-xs text-text-muted">
                {isEditing ? 'Update ward details and settings' : 'Create a new ward with beds and configuration'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
            type="button"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section: Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 bg-primary rounded-full" />
              <h3 className="text-sm font-semibold text-text uppercase tracking-wide">Basic Information</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                ref={firstInputRef}
                label="Ward Name *"
                placeholder="e.g. General Medicine Ward A"
                value={form.wardName}
                onChange={(e) => updateField('wardName', e.target.value)}
                onBlur={() => handleBlur('wardName')}
                error={touched.wardName ? errors.wardName : undefined}
                maxLength={100}
              />
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-text">Ward Code *</label>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => setAutoCode((prev) => !prev)}
                      className={`text-xs font-medium transition-colors ${autoCode ? 'text-primary' : 'text-text-muted hover:text-text'}`}
                    >
                      {autoCode ? 'Auto-generating' : 'Auto-generate'}
                    </button>
                  )}
                </div>
                <Input
                  placeholder="e.g. GMWA"
                  value={form.wardCode}
                  onChange={(e) => {
                    setAutoCode(false);
                    updateField('wardCode', e.target.value.toUpperCase());
                  }}
                  onBlur={() => handleBlur('wardCode')}
                  error={touched.wardCode ? errors.wardCode : undefined}
                  maxLength={20}
                  disabled={autoCode && !isEditing}
                />
                {autoCode && !isEditing && (
                  <p className="text-xs text-text-muted">Code auto-generated from ward name</p>
                )}
              </div>
            </div>

            {/* Ward Type Cards */}
            <div>
              <label className="block mb-[6px] text-sm font-medium text-text">Ward Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(Object.keys(wardTypeConfig) as WardType[]).map((type) => {
                  const config = wardTypeConfig[type];
                  const Icon = config.icon;
                  const isSelected = form.wardType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => updateField('wardType', type)}
                      className={`relative flex flex-col items-center gap-1 px-2 py-2 rounded-lg border-2 transition-all text-center ${
                        isSelected
                          ? `${config.border} ${config.bg} ring-2 ring-primary/30 ring-offset-1 ring-offset-surface`
                          : 'border-border bg-background hover:border-border/80 hover:bg-background/80'
                      }`}
                    >
                      <Icon size={16} className={isSelected ? config.color : 'text-text-muted'} />
                      <span className={`text-[11px] font-semibold leading-tight ${isSelected ? 'text-text' : 'text-text-muted'}`}>
                        {config.label}
                      </span>
                      <span className={`text-[9px] leading-tight ${isSelected ? 'text-text-muted' : 'text-text-muted/60'}`}>
                        {config.description}
                      </span>
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Section: Location & Capacity */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 bg-primary rounded-full" />
              <h3 className="text-sm font-semibold text-text uppercase tracking-wide">Location & Capacity</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1 sm:col-span-2">
                <MultiSelect
                  label={
                    <span className="flex items-center gap-1.5 text-sm font-medium text-text">
                      <DoorOpen size={14} className="text-text-muted" /> Rooms *
                    </span>
                  }
                  values={form.roomIds}
                  onChange={handleRoomsChange}
                  options={rooms.map((room: any) => ({
                    value: room.id,
                    label: room.name,
                    description: room.floor?.name,
                    keywords: room.floor?.name ? [room.floor.name] : [],
                  }))}
                  loading={roomsLoading}
                  placeholder="Select rooms..."
                  searchPlaceholder="Search rooms by name or floor..."
                  emptyText="No rooms configured. Add a room under Rooms first."
                  error={
                    touched.roomIds && errors.roomIds ? (
                      <p className="mt-1 text-xs text-danger flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.roomIds}
                      </p>
                    ) : undefined
                  }
                />
                {!roomsLoading && selectedFloors.length > 0 && (
                  <p className="text-xs text-text-muted pt-1">
                    Floor{selectedFloors.length > 1 ? 's' : ''}: {selectedFloors.join(', ')}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-text">Total Beds *</label>
                <div className="relative">
                  <BedDouble size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={form.totalBeds}
                    onChange={(e) => updateField('totalBeds', Math.max(1, Math.min(500, Number(e.target.value) || 0)))}
                    onBlur={() => handleBlur('totalBeds')}
                    className={`w-full rounded-lg border bg-surface pl-9 pr-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                      touched.totalBeds && errors.totalBeds ? 'border-danger focus:ring-danger/20' : 'border-border'
                    }`}
                  />
                </div>
                {touched.totalBeds && errors.totalBeds && (
                  <p className="text-xs text-danger flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.totalBeds}
                  </p>
                )}
                <p className="text-xs text-text-muted">Total number of beds available in this ward</p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-text">Ward Status</label>
                <button
                  type="button"
                  onClick={() => updateField('isActive', !form.isActive)}
                  className="w-full flex items-center justify-between gap-3 p-2.5 rounded-lg border border-border bg-background/50 hover:bg-background transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${
                        form.isActive ? 'bg-success/10' : 'bg-danger/10'
                      }`}
                    >
                      <Activity size={16} className={form.isActive ? 'text-success' : 'text-danger'} />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-xs font-medium text-text">{form.isActive ? 'Active' : 'Inactive'}</p>
                      <p className="text-[10px] leading-tight text-text-muted truncate">
                        {form.isActive ? 'Accepting patients' : 'Hidden from admission'}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                      form.isActive ? 'bg-success' : 'bg-text-muted/30'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        form.isActive ? 'translate-x-[18px]' : 'translate-x-1'
                      }`}
                    />
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? 'Save Changes' : 'Create Ward'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
